import type { ChartData, QuickChartData } from '@trinity/chat';

export type ValueWithUnit = {
  value: number | string;
  unit?: string;
  isPrefix?: boolean;
  /** When true, renders the full comma-separated number (e.g. 1,450,000). When false (default), abbreviates (e.g. 1.45M). */
  fullFormat?: boolean;
};

/**
 * Abbreviates large numbers to a compact form (e.g. 1450000 → 1.45M).
 * Numbers below 1000 are returned as comma-separated strings.
 */
export function abbreviateNumber(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000_000) {
    const n = Math.floor((abs / 1_000_000_000) * 10) / 10;
    return `${sign}${n}B`;
  }
  if (abs >= 1_000_000) {
    const n = Math.floor((abs / 1_000_000) * 10) / 10;
    return `${sign}${n}M`;
  }
  if (abs >= 1_000) {
    const n = Math.floor((abs / 1_000) * 10) / 10;
    return `${sign}${n}K`;
  }
  return value.toLocaleString();
}

/**
 * Formats a number with comma separators (e.g. 1450000 → 1,450,000).
 */
export function formatWithCommas(value: number): string {
  return value.toLocaleString();
}

export function formatValueWithUnit({ value, unit, isPrefix, fullFormat = false }: ValueWithUnit): string {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  // fullFormat=true → 1,450,000 (full); fullFormat=false (default) → 1.45M (abbreviated)
  let formattedNumber: string;
  if (isNaN(numericValue)) {
    formattedNumber = String(value);
  } else if (fullFormat) {
    formattedNumber = formatWithCommas(numericValue);
  } else {
    formattedNumber = abbreviateNumber(numericValue);
  }

  if (!unit) return formattedNumber;
  return isPrefix ? `${unit}${formattedNumber}` : `${formattedNumber}${unit}`;
}

export function extractUnit(val: string | number): { unit: string; isPrefix: boolean } | null {
  if (typeof val !== 'string') return null;

  const trimmed = val.trim();

  // Check for suffix units (%, °, etc.)
  if (trimmed.endsWith('%')) {
    return { unit: '%', isPrefix: false };
  }
  if (trimmed.endsWith('°')) {
    return { unit: '°', isPrefix: false };
  }

  // Check for prefix units ($, €, £, ¥, etc.)
  const prefixMatches = trimmed.match(/^([$€£¥₹]+)/);
  if (prefixMatches) {
    return { unit: prefixMatches[1], isPrefix: true };
  }

  return null;
}

export function detectUnitFromRecords(
  records: (string | number)[][]
): { unit: string; isPrefix: boolean } | null {
  for (const row of records) {
    for (const val of row) {
      const detected = extractUnit(val);
      if (detected) {
        return detected;
      }
    }
  }
  return null;
}

export function chatDataToQuickChartData(chartData: ChartData): QuickChartData | null {
  const { type } = chartData.chart;
  const chartPayload = chartData.chart.chart;

  switch (type) {
    case 'bar':
      return { type: 'bar', chart: chartPayload as Extract<QuickChartData, { type: 'bar' }>['chart'] };
    case 'stacked-bar':
      return {
        type: 'stacked-bar',
        chart: chartPayload as Extract<QuickChartData, { type: 'stacked-bar' }>['chart'],
      };
    case 'pie':
      return { type: 'pie', chart: chartPayload as Extract<QuickChartData, { type: 'pie' }>['chart'] };
    case 'multiple-clustered-bar':
      return {
        type: 'multiple-clustered-bar',
        chart: chartPayload as Extract<QuickChartData, { type: 'multiple-clustered-bar' }>['chart'],
      };
    case 'multiple-stacked-bar':
      return {
        type: 'multiple-stacked-bar',
        chart: chartPayload as Extract<QuickChartData, { type: 'multiple-stacked-bar' }>['chart'],
      };
    default:
      return null;
  }
}

/**
 * Chart types that use Nivo's Responsive* components and therefore require
 * an explicit-height parent container to render correctly.
 */
export const NIVO_CHART_TYPES = new Set<QuickChartData['type']>([
  'bar',
  'stacked-bar',
  'pie',
  'multiple-clustered-bar',
  'multiple-stacked-bar',
]);
